import Dropzone, { DropzoneOptions } from 'react-dropzone';
import { DropzoneIcon, PlusCircleIcon } from '@synergy/theme';
import { FormattedMessage } from 'react-intl';
import { PropsWithChildren, ReactElement } from 'react';
import { Theme, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { PoolTypography } from '../pool-typography/pool-typography';
import { useIsMobile } from '../hooks';

// https://kovart.github.io/dashed-border-generator/
const background =
    // eslint-disable-next-line
    "data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='rgb(166, 169, 171)' stroke-width='1.5' stroke-dasharray='6%2c 6' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e";

const backgroundOnDrag =
    // eslint-disable-next-line
    "data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='rgb(33, 150, 243)' stroke-width='2' stroke-dasharray='0' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e";

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        position: 'relative',
        // Centred rather than pinned to the top and the bottom: the zone grows
        // with the row it sits in, and icon and caption used to drift apart as
        // it did.
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1.5),
        flex: 1,
        minHeight: '144px', // screendesign
        backgroundImage: `url("${background}")`,
        backgroundColor: '#ffffff',
        borderRadius: 4,
        padding: theme.spacing(2),
        cursor: 'pointer',

        '& svg': {
            width: 57,
            height: 50,
        },

        '&:hover, &:focus': {
            backgroundImage: `url("${backgroundOnDrag}")`,
        },
    },
    disabled: {
        cursor: 'not-allowed',
        backgroundColor: theme.palette.background.default,

        '& svg': {
            color: 'rgba(33, 39, 46, 0.4)',
        },
        '&:hover': {
            backgroundImage: `url("${background}")`,
        },
    },
    caption: {
        color: 'rgba(33, 39, 46, 0.6)',
        width: '100%',
        textAlign: 'center',
        fontSize: '13px',
    },
    onAccept: {
        backgroundImage: `url("${backgroundOnDrag}")`,
        backgroundColor: theme.palette.background.default,
    },
    onReject: {},
    fileList: {
        padding: 0,
        margin: 0,
        listStyle: 'none',
        color: theme.palette.primary.main,

        '& li + li': {
            marginTop: theme.spacing(),
        },

        '& svg': {
            marginRight: theme.spacing(),
        },
    },
    fileContainer: {
        display: 'inline-flex',
        alignItems: 'center',
    },
}));

export type PoolDropzoneProps = DropzoneOptions & {
    acceptedFiles?: File[];
    className?: string;
};

// HINT: accept with MIME type is not working for Chrome with Ubuntu as of Feb. 2023
// if this is fixed, the accept prop can be used instead of custom validation in the onDrop function
export const PoolDropzone = (props: PropsWithChildren<PoolDropzoneProps>): ReactElement => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useIsMobile();

    const { className, children, ...dropzoneProps } = props;
    const getClassNames = (isDragAccept: boolean, isDragActive: boolean, isDragReject: boolean): string => {
        if (props.disabled) {
            return `${classes.root} ${classes.disabled}`;
        }
        return `${classes.root} ${className || ''} ${isDragAccept ? classes.onAccept : ''} ${
            isDragReject ? classes.onReject : ''
        } ${isDragActive ? classes.onAccept : ''}`;
    };

    const fileUpload = isMobile ? 'components.dropzone.fileUploadMobile' : 'components.dropzone.fileUpload';

    return (
        <Dropzone {...dropzoneProps}>
            {({ getRootProps, getInputProps, isDragAccept, isDragActive, isDragReject }) => (
                <div
                    {...getRootProps({
                        className: getClassNames(isDragAccept, isDragActive, isDragReject),
                    })}
                >
                    <input {...getInputProps()} />
                    {isMobile ? (
                        <PlusCircleIcon fontSize="large" sx={{ color: theme.palette.primary.main }} />
                    ) : (
                        <DropzoneIcon />
                    )}
                    <PoolTypography variant="body1" className={classes.caption}>
                        {children || (
                            <FormattedMessage id={isDragActive ? 'components.dropzone.dragState' : fileUpload} />
                        )}
                    </PoolTypography>
                </div>
            )}
        </Dropzone>
    );
};
